import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useCreateSchedule } from "../api/use-schedules";
import type { Schedule } from "../types";
import { WizardCountsCard } from "./wizard-counts-card";
import { WizardStepCourseRequests } from "./wizard-step-course-requests";
import { WizardStepReview } from "./wizard-step-review";

type WizardStep = 1 | 2 | 3;

const DEFAULT_SCHEDULE_NAME = "2026–2027 School Year";

/**
 * Validate the URL using the WHATWG `URL` constructor (matches what the
 * browser does for `<input type="url">` and the server's `[Url]` attribute).
 * Also restricts the protocol to `http:`/`https:` so that `javascript:`,
 * `data:`, `file:`, etc. are rejected up front.
 */
function isValidHttpUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

interface WizardStepperProps {
  currentStep: 1 | 2 | 3;
}

function WizardStepper({ currentStep }: WizardStepperProps) {
  const steps = [
    { num: 1, label: "Outlook calendar" },
    { num: 2, label: "Course requests" },
    { num: 3, label: "Review" },
  ] as const;

  return (
    <div
      className="flex items-center gap-2.5 mb-7"
      data-testid="wizard-stepper"
    >
      {steps.map((step, idx) => {
        const isActive = step.num === currentStep;
        const isComplete = step.num < currentStep;
        const dotClasses = isActive
          ? "bg-brand text-white"
          : isComplete
            ? "bg-green-500/20 text-green-400"
            : "bg-surface-2 text-text-faint";
        return (
          <div key={step.num} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${dotClasses}`}
                data-testid={`wizard-step-${step.num}`}
              >
                {step.num}
              </div>
              <span className="text-[13px] font-medium text-text-soft">
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className="flex-1 h-px bg-border-subtle" />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ValidationErrors {
  name?: string;
  outlookUrl?: string;
}

function validate(name: string, url: string): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!name.trim()) {
    errors.name = "Schedule name is required.";
  }
  const trimmed = url.trim();
  if (!trimmed) {
    errors.outlookUrl = "Outlook share URL is required.";
  } else if (!isValidHttpUrl(trimmed)) {
    errors.outlookUrl =
      "Enter a valid URL beginning with http:// or https:// (e.g. https://outlook.office365.com/…).";
  }
  return errors;
}

export function NewSchedulePage() {
  const navigate = useNavigate();
  const createSchedule = useCreateSchedule();

  const [name, setName] = useState(DEFAULT_SCHEDULE_NAME);
  const [outlookUrl, setOutlookUrl] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [createdSchedule, setCreatedSchedule] = useState<Schedule | null>(null);
  const [step, setStep] = useState<WizardStep>(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const errors = validate(name, outlookUrl);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const result = await createSchedule.mutateAsync({
        name: name.trim(),
        outlookUrl: outlookUrl.trim(),
      });
      setCreatedSchedule(result);
      // Stay on step 1 (counts card view) — the Continue button advances to
      // step 2 explicitly so the user can review the parse results first.
    } catch (err) {
      // Surface the API's ApiResponse.Message when present so the user sees
      // the actual problem (e.g. "Calendar.Parse.NoCourseEvents") instead of
      // axios's generic "Request failed with status code 400".
      let message = "Failed to fetch calendar.";
      const apiBody = (err as { response?: { data?: { message?: string; errorCode?: string } } })
        ?.response?.data;
      if (apiBody?.message) {
        message = apiBody.errorCode
          ? `${apiBody.message} (${apiBody.errorCode})`
          : apiBody.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setServerError(message);
    }
  };

  const isLoading = createSchedule.isPending;
  const hasResult = createdSchedule !== null;

  return (
    <div className="min-h-[calc(100vh-3.5rem-4rem)] animate-fade-in">
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-2 text-sm text-text-muted mb-6"
      >
        <Link
          to="/schedules"
          data-testid="breadcrumb-schedules"
          className="inline-flex items-center gap-1.5 hover:text-white"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Schedules
        </Link>
        <span className="text-text-faint">/</span>
        <span className="text-white font-medium">New Schedule</span>
      </nav>

      <div className="max-w-[560px] mx-auto">
        <WizardStepper currentStep={step} />

        {hasResult && createdSchedule && step === 1 && (
          <WizardCountsCard
            schedule={createdSchedule}
            onContinue={() => setStep(2)}
          />
        )}

        {hasResult && createdSchedule && step === 2 && (
          <WizardStepCourseRequests
            scheduleId={createdSchedule.id}
            onContinue={() => setStep(3)}
          />
        )}

        {hasResult && createdSchedule && step === 3 && (
          <WizardStepReview
            scheduleId={createdSchedule.id}
            onFinish={() => navigate(`/schedules/${createdSchedule.id}`)}
          />
        )}

        {!hasResult && isLoading && (
          <div
            data-testid="wizard-loading"
            className="bg-surface border border-border-subtle rounded-lg p-6"
          >
            <div className="text-center py-8">
              <div className="flex justify-center mb-4">
                <LoadingSpinner size="lg" />
              </div>
              <p className="text-sm font-medium text-text-soft">
                Fetching calendar…
              </p>
              <p className="text-[13px] text-text-faint mt-1">
                Reading events and parsing descriptions.
              </p>
            </div>
          </div>
        )}

        {!hasResult && !isLoading && (
          <form
            onSubmit={handleSubmit}
            data-testid="wizard-step-1-form"
            className="bg-surface border border-border-subtle rounded-lg p-6 animate-fade-in"
            noValidate
          >
            <h2 className="text-lg font-semibold text-white mb-1">
              Connect an Outlook calendar
            </h2>
            <p className="text-[13px] text-text-muted mb-5">
              Paste the published share URL of the calendar Abbie built. We'll
              only read events that contain a Course ID in their description.
            </p>

            {serverError && (
              <div
                data-testid="wizard-server-error"
                className="mb-4 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400"
                role="alert"
              >
                {serverError}
              </div>
            )}

            <label
              htmlFor="outlook-url"
              className="block text-[13px] font-medium text-text-soft mb-1.5"
            >
              Outlook share URL
            </label>
            <input
              id="outlook-url"
              type="url"
              value={outlookUrl}
              onChange={(e) => setOutlookUrl(e.target.value)}
              placeholder="https://outlook.office365.com/owa/calendar/master@butlertech.org/.../calendar.ics"
              data-testid="outlook-url-input"
              aria-invalid={!!validationErrors.outlookUrl}
              aria-describedby={
                validationErrors.outlookUrl
                  ? "outlook-url-error"
                  : "outlook-url-help"
              }
              className="w-full bg-page border border-border-subtle rounded-md px-3 py-2 text-[13px] text-white placeholder:text-text-faint font-mono focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
            {validationErrors.outlookUrl ? (
              <p
                id="outlook-url-error"
                data-testid="outlook-url-error"
                className="mt-1.5 mb-5 text-xs text-red-400"
              >
                {validationErrors.outlookUrl}
              </p>
            ) : (
              <p
                id="outlook-url-help"
                className="mt-1.5 mb-5 text-xs text-text-faint"
              >
                Calendar → Share → Publish → copy the ICS link.
              </p>
            )}

            <label
              htmlFor="schedule-name"
              className="block text-[13px] font-medium text-text-soft mb-1.5"
            >
              Schedule name
            </label>
            <input
              id="schedule-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="schedule-name-input"
              aria-invalid={!!validationErrors.name}
              aria-describedby={
                validationErrors.name ? "schedule-name-error" : undefined
              }
              className="w-full bg-page border border-border-subtle rounded-md px-3 py-2 text-sm text-white placeholder:text-text-faint focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
            {validationErrors.name && (
              <p
                id="schedule-name-error"
                data-testid="schedule-name-error"
                className="mt-1.5 text-xs text-red-400"
              >
                {validationErrors.name}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-[22px]">
              <button
                type="button"
                onClick={() => navigate("/schedules")}
                data-testid="wizard-cancel"
                className="bg-surface-2 hover:bg-surface-2/80 text-text-soft border-none rounded-md px-[18px] py-[9px] font-medium text-sm cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="wizard-submit"
                className="bg-brand hover:bg-brand-hover text-white border-none rounded-md px-[18px] py-[9px] font-semibold text-sm cursor-pointer disabled:opacity-50"
                disabled={isLoading}
              >
                Fetch calendar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
